import AddShoppingCartRoundedIcon from "@mui/icons-material/AddShoppingCartRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import ShoppingCartCheckoutRoundedIcon from "@mui/icons-material/ShoppingCartCheckoutRounded";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, {
  ChangeEvent,
  KeyboardEvent,
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from "react";
import { brandColors } from "../../App";
import {
  fetchProductList,
  fetchStoreList,
  submitStoreRequest,
} from "../../services/inventoryService";

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface Product {
  product_id: string;
  name: string;
  description?: string;
  category?: string;
  sub_category?: string;
  supplier?: string;
  packed?: number | string;
  unit?: string;
  store_visible?: boolean;
}

interface ProductSubcategory {
  name: string;
  products: Product[];
}

interface ProductCategory {
  name: string;
  subcategories: ProductSubcategory[];
  productCount: number;
}

interface StoreApiResponse {
  store_id: string;
  name: string;
}

interface StoreOption {
  label: string;
  value: string;
}

interface SelectedRequestProduct {
  product_id: string;
  name: string;
  packed?: number | string;
  unit?: string;
  qty_packed: number;
  qty_unpacked: number;
  saved: boolean;
}

interface StoredRequestCart {
  version: 1;
  startedAt: number;
  items: SelectedRequestProduct[];
  notes: string;
  receivingStore: StoreOption | null;
}

type RequestQuantityField = "qty_packed" | "qty_unpacked";

interface ProductCardProps {
  product: Product;
  selected?: SelectedRequestProduct;
  onQuantityChange: (
    product: Product,
    field: RequestQuantityField,
    quantity: number,
  ) => void;
  onAdd: (product: Product) => void;
}

interface RequestSummaryProps {
  requestItems: SelectedRequestProduct[];
  totalRequestPacks: number;
  notes: string;
  receivingStoreSelected: boolean;
  onNotesChange: (value: string) => void;
  onRemove: (productId: string) => void;
  onReview: () => void;
  onReset: () => void;
}

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const DEFAULT_WAREHOUSE_ID = "febbc26a-dff7-4220-91ad-319eaea4d511";

const UNKNOWN_CATEGORY = "Uncategorized";
const UNKNOWN_SUBCATEGORY = "General";

const REQUEST_CART_STORAGE_KEY = "store-request-cart-v1";
const REQUEST_CART_STORAGE_VERSION = 1;
const REQUEST_CART_TTL_MS = 8 * 60 * 60 * 1000;

// -----------------------------------------------------------------------------
// Utility functions
// -----------------------------------------------------------------------------

/**
 * Converts nullable values into safe display text.
 */
const displayValue = (value: unknown, fallback = "—"): string => {
  if (value === null || value === undefined || value === "") {
    return fallback;
  }

  return String(value);
};

/**
 * Keeps packed quantities as non-negative whole pack counts.
 */
const normalizePackedQuantity = (value: number): number => {
  if (!Number.isFinite(value) || value < 0) {
    return 0;
  }

  return Math.floor(value);
};

/**
 * Keeps 1-unit pack quantities as non-negative whole pack counts.
 */
const normalizeUnpackedQuantity = (value: number): number => {
  if (!Number.isFinite(value) || value < 0) {
    return 0;
  }

  return Math.round((value + Number.EPSILON) * 100) / 100;
};

/**
 * Formats the configured pack size without unnecessary trailing zeroes.
 * Example: 2.900 -> 2.9.
 */
const formatPackSize = (packed: number | string | undefined): string => {
  const parsed = Number(packed);

  if (Number.isFinite(parsed)) {
    return String(parsed);
  }

  const value = String(packed ?? "").trim();
  return value || "1";
};

/**
 * Returns a normalized unit label for pack descriptions.
 */
const formatPackUnit = (unit: string | undefined): string => {
  return unit?.trim().toUpperCase() || "UNIT";
};

/**
 * Creates the display label for the configured product pack.
 * Example: 2.9 KG Pack.
 */
const getConfiguredPackLabel = (
  packed: number | string | undefined,
  unit: string | undefined,
): string => {
  return `${formatPackSize(packed)} ${formatPackUnit(unit)} Pack`;
};

/**
 * Creates the display label for the standard 1-unit pack.
 * Example: 1 KG Pack.
 */
const getSingleUnitPackLabel = (unit: string | undefined): string => {
  return `1 ${formatPackUnit(unit)} Pack`;
};

/**
 * Validates a product restored from localStorage.
 */
const isStoredRequestProduct = (
  value: unknown,
): value is SelectedRequestProduct => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const product = value as Partial<SelectedRequestProduct> & {
    qty?: unknown;
  };

  const legacyPackedQuantity =
    typeof product.qty === "number" && Number.isFinite(product.qty)
      ? product.qty
      : 0;

  const packedQuantity =
    typeof product.qty_packed === "number" &&
    Number.isFinite(product.qty_packed)
      ? product.qty_packed
      : legacyPackedQuantity;

  const unpackedQuantity =
    typeof product.qty_unpacked === "number" &&
    Number.isFinite(product.qty_unpacked)
      ? product.qty_unpacked
      : 0;

  return (
    typeof product.product_id === "string" &&
    typeof product.name === "string" &&
    (packedQuantity > 0 || unpackedQuantity > 0)
  );
};

/**
 * Validates a receiving-store option restored from localStorage.
 */
const isStoredStoreOption = (value: unknown): value is StoreOption => {
  if (!value || typeof value !== "object") {
    return false;
  }

  const store = value as Partial<StoreOption>;

  return typeof store.label === "string" && typeof store.value === "string";
};

/**
 * Removes the persisted request cart.
 */
const clearStoredRequestCart = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.removeItem(REQUEST_CART_STORAGE_KEY);
  } catch (error) {
    console.warn("Unable to clear the saved request cart:", error);
  }
};

/**
 * Restores the request cart only when it is still within its 8-hour lifetime.
 * The lifetime is always measured from the first item that started the cart.
 */
const loadStoredRequestCart = (): StoredRequestCart | null => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const rawCart = window.localStorage.getItem(REQUEST_CART_STORAGE_KEY);

    if (!rawCart) {
      return null;
    }

    const parsedCart = JSON.parse(rawCart) as Partial<StoredRequestCart>;

    if (
      parsedCart.version !== REQUEST_CART_STORAGE_VERSION ||
      typeof parsedCart.startedAt !== "number" ||
      !Number.isFinite(parsedCart.startedAt) ||
      !Array.isArray(parsedCart.items)
    ) {
      clearStoredRequestCart();
      return null;
    }

    const expiresAt = parsedCart.startedAt + REQUEST_CART_TTL_MS;

    if (Date.now() >= expiresAt) {
      clearStoredRequestCart();
      return null;
    }

    const items = parsedCart.items
      .filter(isStoredRequestProduct)
      .map((item) => {
        const storedItem = item as SelectedRequestProduct & {
          qty?: number;
        };

        return {
          product_id: storedItem.product_id,
          name: storedItem.name,
          qty_packed: normalizePackedQuantity(
            storedItem.qty_packed ?? storedItem.qty ?? 0,
          ),
          qty_unpacked: normalizeUnpackedQuantity(storedItem.qty_unpacked ?? 0),
          saved: true,
        };
      });

    if (items.length === 0) {
      clearStoredRequestCart();
      return null;
    }

    return {
      version: REQUEST_CART_STORAGE_VERSION,
      startedAt: parsedCart.startedAt,
      items,
      notes: typeof parsedCart.notes === "string" ? parsedCart.notes : "",
      receivingStore: isStoredStoreOption(parsedCart.receivingStore)
        ? parsedCart.receivingStore
        : null,
    };
  } catch (error) {
    console.warn("Unable to restore the saved request cart:", error);
    clearStoredRequestCart();
    return null;
  }
};

/**
 * Saves the request cart without changing the original start time.
 */
const saveStoredRequestCart = (cart: StoredRequestCart): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.localStorage.setItem(REQUEST_CART_STORAGE_KEY, JSON.stringify(cart));
  } catch (error) {
    console.warn("Unable to save the request cart:", error);
  }
};

/**
 * Creates the category and subcategory hierarchy in one pass.
 */
const groupProducts = (products: Product[]): ProductCategory[] => {
  const categoryMap = new Map<string, Map<string, Product[]>>();

  products.forEach((product) => {
    const categoryName = product.category?.trim() || UNKNOWN_CATEGORY;

    const subcategoryName = product.sub_category?.trim() || UNKNOWN_SUBCATEGORY;

    let subcategoryMap = categoryMap.get(categoryName);

    if (!subcategoryMap) {
      subcategoryMap = new Map<string, Product[]>();
      categoryMap.set(categoryName, subcategoryMap);
    }

    let groupedProducts = subcategoryMap.get(subcategoryName);

    if (!groupedProducts) {
      groupedProducts = [];
      subcategoryMap.set(subcategoryName, groupedProducts);
    }

    groupedProducts.push(product);
  });

  return Array.from(categoryMap.entries())
    .map(([categoryName, subcategoryMap]) => {
      const subcategories = Array.from(subcategoryMap.entries())
        .map(([subcategoryName, groupedProducts]) => ({
          name: subcategoryName,
          products: [...groupedProducts].sort((first, second) =>
            first.name.localeCompare(second.name),
          ),
        }))
        .sort((first, second) => first.name.localeCompare(second.name));

      return {
        name: categoryName,
        subcategories,
        productCount: subcategories.reduce(
          (total, subcategory) => total + subcategory.products.length,
          0,
        ),
      };
    })
    .sort((first, second) => first.name.localeCompare(second.name));
};

const getPackedUnitPackLabel = (
  packed?: number | string,
  unit?: string,
): string => {
  const packSize = Number(packed) || 0;
  const normalizedUnit = unit?.trim().toUpperCase() || "UNIT";

  return `${packSize} ${normalizedUnit} Pack`;
};

/**
 * Creates searchable text for a product.
 */
const getProductSearchText = (product: Product): string => {
  return [
    product.name,
    product.description,
    product.category,
    product.sub_category,
    product.supplier,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

// -----------------------------------------------------------------------------
// Product card
// -----------------------------------------------------------------------------

const ProductCard: React.FC<ProductCardProps> = memo(
  ({ product, selected, onQuantityChange, onAdd }) => {
    const packedQuantity = selected?.qty_packed ?? 0;
    const unpackedQuantity = selected?.qty_unpacked ?? 0;
    const hasQuantity = packedQuantity > 0 || unpackedQuantity > 0;
    const isAdded = Boolean(selected?.saved && hasQuantity);

    /**
     * Decreases the packed quantity by one pack.
     */
    const handleDecreasePacked = () => {
      onQuantityChange(
        product,
        "qty_packed",
        normalizePackedQuantity(packedQuantity - 1),
      );
    };

    /**
     * Increases the packed quantity by one pack.
     */
    const handleIncreasePacked = () => {
      onQuantityChange(product, "qty_packed", packedQuantity + 1);
    };

    /**
     * Updates packed quantity using direct input.
     */
    const handlePackedQuantityInput = (
      event: ChangeEvent<HTMLInputElement>,
    ) => {
      onQuantityChange(
        product,
        "qty_packed",
        normalizePackedQuantity(Number(event.target.value)),
      );
    };

    /**
     * Updates the quantity of standard 1-unit packs.
     */
    const handleUnpackedQuantityInput = (
      event: ChangeEvent<HTMLInputElement>,
    ) => {
      onQuantityChange(
        product,
        "qty_unpacked",
        normalizeUnpackedQuantity(Number(event.target.value)),
      );
    };

    return (
      <Card
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          height: "100%",
          boxSizing: "border-box",
          borderRadius: 3,
          borderColor: isAdded ? "success.light" : "divider",
          bgcolor: isAdded ? "rgba(46, 125, 50, 0.04)" : "background.paper",
          transition:
            "border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease",
          "&:hover": {
            borderColor: isAdded ? "success.main" : "primary.main",
            boxShadow: 2,
            transform: "translateY(-2px)",
          },
        }}
      >
        <CardContent
          sx={{
            p: 1.75,
            "&:last-child": {
              pb: 1.75,
            },
          }}
        >
          <Stack spacing={1.5}>
            <Stack
              direction="row"
              spacing={1.25}
              alignItems="flex-start"
              sx={{
                width: "100%",
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 2,
                  bgcolor: isAdded ? "success.main" : "primary.main",
                  color: "common.white",
                  fontWeight: 800,
                }}
              >
                {product.name.trim().charAt(0).toUpperCase() || "P"}
              </Box>

              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Stack
                  direction="row"
                  spacing={0.75}
                  alignItems="flex-start"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      minWidth: 0,
                      fontWeight: 800,
                      lineHeight: 1.3,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {product.name}
                  </Typography>

                  {isAdded && (
                    <Tooltip title="Added to request">
                      <CheckCircleRoundedIcon
                        color="success"
                        fontSize="small"
                        sx={{ flexShrink: 0 }}
                      />
                    </Tooltip>
                  )}
                </Stack>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: "-webkit-box",
                    mt: 0.35,
                    overflow: "hidden",
                    overflowWrap: "anywhere",
                    WebkitBoxOrient: "vertical",
                    WebkitLineClamp: 2,
                  }}
                >
                  {displayValue(
                    product.description,
                    "No description available",
                  )}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              <Chip
                size="small"
                variant="outlined"
                label={`Pack: ${displayValue(
                  product.packed,
                  "0",
                )} ${displayValue(product.unit, "")}`.trim()}
              />

              {/* {product.supplier && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={product.supplier}
                />
              )} */}
            </Stack>

            <Divider />

            <Stack spacing={1}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", sm: "flex-end" }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.5, fontWeight: 700 }}
                  >
                    {/* {getConfiguredPackLabel(product.packed, product.unit)} */}
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label={getPackedUnitPackLabel(product.packed, product.unit)}
                    value={packedQuantity}
                    onChange={handlePackedQuantityInput}
                    slotProps={{
                      htmlInput: {
                        min: 0,
                        step: 1,
                        inputMode: "numeric",
                        "aria-label": `${product.name} ${getPackedUnitPackLabel(
                          product.packed,
                          product.unit,
                        )} quantity`,
                      },
                    }}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      maxWidth: { sm: 150 },
                    }}
                  />
                </Box>

                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label={getSingleUnitPackLabel(product.unit)}
                  value={unpackedQuantity}
                  onChange={handleUnpackedQuantityInput}
                  slotProps={{
                    htmlInput: {
                      min: 0,
                      step: 0.01,
                      inputMode: "numeric",
                      "aria-label": `${product.name} ${getSingleUnitPackLabel(
                        product.unit,
                      )} quantity`,
                    },
                  }}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    maxWidth: { sm: 150 },
                  }}
                />
              </Stack>

              <Button
                fullWidth
                variant={isAdded ? "outlined" : "contained"}
                color={isAdded ? "success" : "primary"}
                disabled={!hasQuantity}
                startIcon={
                  isAdded ? (
                    <CheckCircleRoundedIcon />
                  ) : (
                    <AddShoppingCartRoundedIcon />
                  )
                }
                onClick={() => onAdd(product)}
                sx={{
                  minHeight: 40,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 750,
                  whiteSpace: "nowrap",
                }}
              >
                {isAdded ? "In request" : "Add"}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  },
);
ProductCard.displayName = "ProductCard";

// -----------------------------------------------------------------------------
// Desktop request summary
// -----------------------------------------------------------------------------

const RequestSummary: React.FC<RequestSummaryProps> = memo(
  ({
    requestItems,
    totalRequestPacks,
    notes,
    receivingStoreSelected,
    onNotesChange,
    onRemove,
    onReview,
    onReset,
  }) => {
    return (
      <Paper
        variant="outlined"
        sx={{
          width: "100%",
          minWidth: 0,
          position: "sticky",
          top: 16,
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <Box sx={{ p: 2 }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h6" fontWeight={800}>
                Current Request
              </Typography>

              <Typography variant="body2" color="text.secondary">
                {requestItems.length} products · {totalRequestPacks} packs
              </Typography>
            </Box>

            <Chip
              icon={<ShoppingCartCheckoutRoundedIcon />}
              label={requestItems.length}
              color={requestItems.length > 0 ? "success" : "default"}
            />
          </Stack>
        </Box>

        <Divider />

        <Stack
          spacing={1}
          sx={{
            minHeight: 130,
            maxHeight: 340,
            overflowY: "auto",
            p: 1.5,
          }}
        >
          {requestItems.length === 0 ? (
            <Box
              sx={{
                py: 3,
                px: 1,
                textAlign: "center",
              }}
            >
              <Inventory2OutlinedIcon
                sx={{
                  fontSize: 38,
                  color: "text.disabled",
                }}
              />

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.75 }}
              >
                Products added to the request will appear here.
              </Typography>
            </Box>
          ) : (
            requestItems.map((item) => (
              <Paper
                key={item.product_id}
                variant="outlined"
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={750} noWrap>
                      {item.name}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      {item.qty_packed > 0 && (
                        <>
                          {item.qty_packed} ×{" "}
                          {getConfiguredPackLabel(item.packed, item.unit)}
                        </>
                      )}
                      {item.qty_packed > 0 && item.qty_unpacked > 0 && " · "}
                      {item.qty_unpacked > 0 && (
                        <>
                          {item.qty_unpacked} ×{" "}
                          {getSingleUnitPackLabel(item.unit)}
                        </>
                      )}
                    </Typography>
                  </Box>

                  <Tooltip title="Remove from request">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => onRemove(item.product_id)}
                    >
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Paper>
            ))
          )}
        </Stack>

        <Divider />

        <Stack spacing={1.25} sx={{ p: 1.5 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            size="small"
            label="Request notes"
            placeholder="Add delivery or handling instructions"
            value={notes}
            onChange={(event) => onNotesChange(event.target.value)}
          />

          {!receivingStoreSelected && (
            <Alert severity="warning">
              Select a receiving store before reviewing.
            </Alert>
          )}

          <Button
            fullWidth
            variant="contained"
            startIcon={<ShoppingCartCheckoutRoundedIcon />}
            disabled={requestItems.length === 0 || !receivingStoreSelected}
            onClick={onReview}
            sx={{
              minHeight: 44,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 750,
            }}
          >
            Review request
          </Button>

          <Button
            fullWidth
            color="inherit"
            variant="text"
            disabled={requestItems.length === 0 && notes.length === 0}
            onClick={onReset}
            sx={{
              textTransform: "none",
            }}
          >
            Clear request
          </Button>
        </Stack>
      </Paper>
    );
  },
);

RequestSummary.displayName = "RequestSummary";

// -----------------------------------------------------------------------------
// Main component
// -----------------------------------------------------------------------------

const RequestInventory: React.FC = () => {
  const theme = useTheme();

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [inventory, setInventory] = useState<Product[]>([]);
  const [stores, setStores] = useState<StoreOption[]>([]);

  const [selectedWarehouse, setSelectedWarehouse] =
    useState<StoreOption | null>(null);

  const [selectedReceivingStore, setSelectedReceivingStore] =
    useState<StoreOption | null>(null);

  const [selectedProducts, setSelectedProducts] = useState<
    SelectedRequestProduct[]
  >([]);

  const [activeCategory, setActiveCategory] = useState<string>("");

  const [activeSubcategory, setActiveSubcategory] = useState<string>("");

  const [search, setSearch] = useState<string>("");
  const [notes, setNotes] = useState<string>("");

  const [cartStartedAt, setCartStartedAt] = useState<number | null>(null);
  const [cartHydrated, setCartHydrated] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const [reviewOpen, setReviewOpen] = useState<boolean>(false);

  const [resetDialogOpen, setResetDialogOpen] = useState<boolean>(false);

  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "warning" | "info";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const deferredSearch = useDeferredValue(search);

  /**
   * Creates fast selected-product access for cards.
   */
  const selectedProductMap = useMemo(() => {
    return new Map(
      selectedProducts.map((product) => [product.product_id, product]),
    );
  }, [selectedProducts]);

  /**
   * Limits the catalogue to store-visible products.
   */
  const storeVisibleProducts = useMemo(() => {
    return inventory.filter((product) => product.store_visible === true);
  }, [inventory]);

  /**
   * Filters product results without blocking search input.
   */
  const filteredProducts = useMemo(() => {
    const normalizedSearch = deferredSearch.trim().toLowerCase();

    if (!normalizedSearch) {
      return storeVisibleProducts;
    }

    return storeVisibleProducts.filter((product) =>
      getProductSearchText(product).includes(normalizedSearch),
    );
  }, [deferredSearch, storeVisibleProducts]);

  /**
   * Builds the category hierarchy from filtered products.
   */
  const groupedProducts = useMemo(() => {
    return groupProducts(filteredProducts);
  }, [filteredProducts]);

  /**
   * Returns the selected category or the first available category.
   */
  const selectedCategory = useMemo(() => {
    return (
      groupedProducts.find((category) => category.name === activeCategory) ??
      groupedProducts[0] ??
      null
    );
  }, [activeCategory, groupedProducts]);

  /**
   * Returns the selected subcategory or the first available one.
   */
  const selectedSubcategory = useMemo(() => {
    if (!selectedCategory) {
      return null;
    }

    return (
      selectedCategory.subcategories.find(
        (subcategory) => subcategory.name === activeSubcategory,
      ) ??
      selectedCategory.subcategories[0] ??
      null
    );
  }, [activeSubcategory, selectedCategory]);

  /**
   * Contains only products explicitly added to the request.
   */
  const requestItems = useMemo(() => {
    return selectedProducts.filter(
      (product) =>
        product.saved && (product.qty_packed > 0 || product.qty_unpacked > 0),
    );
  }, [selectedProducts]);

  /**
   * Counts all requested packs regardless of pack size.
   */
  const totalRequestPacks = useMemo(() => {
    return requestItems.reduce(
      (total, product) => total + product.qty_packed + product.qty_unpacked,
      0,
    );
  }, [requestItems]);

  /**
   * Displays a Material UI notification.
   */
  const showNotification = useCallback(
    (message: string, severity: "success" | "error" | "warning" | "info") => {
      setNotification({
        open: true,
        message,
        severity,
      });
    },
    [],
  );

  /**
   * Clears both the in-memory cart and its localStorage copy.
   */
  const clearCurrentRequest = useCallback((): void => {
    setSelectedProducts([]);
    setSelectedReceivingStore(null);
    setNotes("");
    setReviewOpen(false);
    setResetDialogOpen(false);
    setCartStartedAt(null);
    clearStoredRequestCart();
  }, []);

  /**
   * Loads products, warehouses, and receiving stores concurrently.
   */
  const loadPageData = useCallback(async (): Promise<void> => {
    setLoading(true);

    try {
      const [productData, warehouseData, storeData] = await Promise.all([
        fetchProductList(""),
        fetchStoreList("WAREHOUSE"),
        fetchStoreList("STORE"),
      ]);

      const products: Product[] = Array.isArray(productData) ? productData : [];

      const warehouseOptions: StoreOption[] = (
        Array.isArray(warehouseData) ? warehouseData : []
      ).map((item: StoreApiResponse) => ({
        label: item.name,
        value: item.store_id,
      }));

      const storeOptions: StoreOption[] = (
        Array.isArray(storeData) ? storeData : []
      ).map((item: StoreApiResponse) => ({
        label: item.name,
        value: item.store_id,
      }));

      const visibleProducts = products.filter(
        (product) => product.store_visible === true,
      );

      const groupedVisibleProducts = groupProducts(visibleProducts);

      const defaultWarehouse =
        warehouseOptions.find(
          (warehouse) => warehouse.value === DEFAULT_WAREHOUSE_ID,
        ) ??
        warehouseOptions[0] ??
        null;

      setInventory(products);
      setStores(storeOptions);
      setSelectedWarehouse(defaultWarehouse);

      setActiveCategory(groupedVisibleProducts[0]?.name ?? "");

      setActiveSubcategory(
        groupedVisibleProducts[0]?.subcategories[0]?.name ?? "",
      );
    } catch (error) {
      console.error("Unable to load store request data:", error);

      showNotification(
        "Unable to load products or store information.",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    void loadPageData();
  }, [loadPageData]);

  /**
   * Restores an unexpired cart once when this page is mounted.
   */
  useEffect(() => {
    const storedCart = loadStoredRequestCart();

    if (storedCart) {
      setSelectedProducts(storedCart.items);
      setSelectedReceivingStore(storedCart.receivingStore);
      setNotes(storedCart.notes);
      setCartStartedAt(storedCart.startedAt);
    }

    setCartHydrated(true);
  }, []);

  /**
   * Keeps a restored receiving store synchronized with the latest store list.
   */
  /**
   * Backfills pack size and unit for carts saved by the previous UI version.
   */
  useEffect(() => {
    if (!cartHydrated || inventory.length === 0) {
      return;
    }

    setSelectedProducts((currentProducts) =>
      currentProducts.map((item) => {
        const product = inventory.find(
          (inventoryProduct) => inventoryProduct.product_id === item.product_id,
        );

        if (!product) {
          return item;
        }

        if (item.packed === product.packed && item.unit === product.unit) {
          return item;
        }

        return {
          ...item,
          packed: product.packed,
          unit: product.unit,
        };
      }),
    );
  }, [cartHydrated, inventory]);

  useEffect(() => {
    if (!cartHydrated || !selectedReceivingStore || stores.length === 0) {
      return;
    }

    const currentStore = stores.find(
      (store) => store.value === selectedReceivingStore.value,
    );

    if (!currentStore) {
      setSelectedReceivingStore(null);
      return;
    }

    if (currentStore.label !== selectedReceivingStore.label) {
      setSelectedReceivingStore(currentStore);
    }
  }, [cartHydrated, selectedReceivingStore, stores]);

  /**
   * Persists cart changes without ever extending the original 8-hour window.
   */
  useEffect(() => {
    if (!cartHydrated) {
      return;
    }

    if (requestItems.length === 0) {
      clearStoredRequestCart();

      if (cartStartedAt !== null) {
        setCartStartedAt(null);
      }

      return;
    }

    if (cartStartedAt === null) {
      return;
    }

    saveStoredRequestCart({
      version: REQUEST_CART_STORAGE_VERSION,
      startedAt: cartStartedAt,
      items: requestItems,
      notes,
      receivingStore: selectedReceivingStore,
    });
  }, [
    cartHydrated,
    cartStartedAt,
    notes,
    requestItems,
    selectedReceivingStore,
  ]);

  /**
   * Expires the cart after 8 hours even when the page remains open.
   */
  useEffect(() => {
    if (!cartHydrated || cartStartedAt === null || requestItems.length === 0) {
      return;
    }

    const expiresAt = cartStartedAt + REQUEST_CART_TTL_MS;
    const remainingMs = expiresAt - Date.now();

    if (remainingMs <= 0) {
      clearCurrentRequest();
      showNotification(
        "The saved request expired after 8 hours and was cleared.",
        "info",
      );
      return;
    }

    const timeoutId = window.setTimeout(() => {
      clearCurrentRequest();
      showNotification(
        "The saved request expired after 8 hours and was cleared.",
        "info",
      );
    }, remainingMs);

    return () => window.clearTimeout(timeoutId);
  }, [
    cartHydrated,
    cartStartedAt,
    clearCurrentRequest,
    requestItems.length,
    showNotification,
  ]);

  /**
   * Keeps category selection valid after search results change.
   */
  useEffect(() => {
    if (groupedProducts.length === 0) {
      setActiveCategory("");
      setActiveSubcategory("");
      return;
    }

    const currentCategoryStillExists = groupedProducts.some(
      (category) => category.name === activeCategory,
    );

    if (!currentCategoryStillExists) {
      const firstCategory = groupedProducts[0];

      setActiveCategory(firstCategory.name);
      setActiveSubcategory(firstCategory.subcategories[0]?.name ?? "");
    }
  }, [activeCategory, groupedProducts]);

  /**
   * Selects a category and its first subcategory.
   */
  const handleCategorySelect = useCallback((category: ProductCategory) => {
    setActiveCategory(category.name);
    setActiveSubcategory(category.subcategories[0]?.name ?? "");
  }, []);

  /**
   * Selects a subcategory.
   */
  const handleSubcategorySelect = useCallback((subcategoryName: string) => {
    setActiveSubcategory(subcategoryName);
  }, []);

  /**
   * Updates packed or unpacked quantity while preserving its added state.
   */
  const handleQuantityChange = useCallback(
    (product: Product, field: RequestQuantityField, quantity: number): void => {
      const normalizedQuantity =
        field === "qty_packed"
          ? normalizePackedQuantity(quantity)
          : normalizeUnpackedQuantity(quantity);

      setSelectedProducts((currentProducts) => {
        const existingProduct = currentProducts.find(
          (item) => item.product_id === product.product_id,
        );

        if (!existingProduct) {
          return [
            ...currentProducts,
            {
              product_id: product.product_id,
              name: product.name,
              packed: product.packed,
              unit: product.unit,
              qty_packed: field === "qty_packed" ? normalizedQuantity : 0,
              qty_unpacked: field === "qty_unpacked" ? normalizedQuantity : 0,
              saved: false,
            },
          ];
        }

        return currentProducts.map((item) => {
          if (item.product_id !== product.product_id) {
            return item;
          }

          const updatedProduct = {
            ...item,
            packed: product.packed,
            unit: product.unit,
            [field]: normalizedQuantity,
          };

          const hasQuantity =
            updatedProduct.qty_packed > 0 || updatedProduct.qty_unpacked > 0;

          return {
            ...updatedProduct,
            saved: hasQuantity ? item.saved : false,
          };
        });
      });
    },
    [],
  );

  /**
   * Adds the entered quantity to the request.
   */
  const handleAddProduct = useCallback(
    (product: Product): void => {
      const selectedProduct = selectedProductMap.get(product.product_id);

      if (
        !selectedProduct ||
        (selectedProduct.qty_packed <= 0 && selectedProduct.qty_unpacked <= 0)
      ) {
        return;
      }

      /**
       * Start the 8-hour window only once: when the first product is actually
       * added to the cart. Additional cart activity does not refresh it.
       */
      setCartStartedAt((currentStartedAt) => currentStartedAt ?? Date.now());

      setSelectedProducts((currentProducts) =>
        currentProducts.map((item) =>
          item.product_id === product.product_id
            ? {
                ...item,
                saved: true,
              }
            : item,
        ),
      );

      showNotification(`${product.name} added to the request.`, "success");
    },
    [selectedProductMap, showNotification],
  );

  /**
   * Removes a product from the request.
   */
  const handleRemoveRequestItem = useCallback((productId: string): void => {
    setSelectedProducts((currentProducts) =>
      currentProducts.filter((product) => product.product_id !== productId),
    );
  }, []);

  /**
   * Opens reset confirmation.
   */
  const handleResetRequest = useCallback(() => {
    setResetDialogOpen(true);
  }, []);

  /**
   * Clears the current request after confirmation.
   */
  const handleConfirmReset = useCallback(() => {
    clearCurrentRequest();
    showNotification("Request cleared.", "info");
  }, [clearCurrentRequest, showNotification]);

  /**
   * Updates the selected receiving store.
   */
  const handleReceivingStoreChange = useCallback(
    (event: SelectChangeEvent<string>): void => {
      const receivingStore = stores.find(
        (store) => store.value === event.target.value,
      );

      setSelectedReceivingStore(receivingStore ?? null);
    },
    [stores],
  );

  /**
   * Validates the request before opening review.
   */
  const handleOpenReview = useCallback((): void => {
    if (!selectedReceivingStore?.value) {
      showNotification("Select a receiving store.", "warning");
      return;
    }

    if (!selectedWarehouse?.value) {
      showNotification("No sending warehouse is available.", "warning");
      return;
    }

    if (requestItems.length === 0) {
      showNotification("Add at least one product to the request.", "warning");
      return;
    }

    setReviewOpen(true);
  }, [
    requestItems.length,
    selectedReceivingStore,
    selectedWarehouse,
    showNotification,
  ]);

  /**
   * Submits the request using the existing backend payload.
   */
  const handleSubmit = useCallback(async (): Promise<void> => {
    if (
      !selectedReceivingStore?.value ||
      !selectedWarehouse?.value ||
      requestItems.length === 0
    ) {
      return;
    }

    setSubmitting(true);

    try {
      const payload = requestItems.map((product) => ({
        ...product,
        // Keep qty for backward compatibility with the existing request service.
        qty: product.qty_packed,
        qty_packed: product.qty_packed,
        qty_unpacked: product.qty_unpacked,
        notes,
      }));

      const response = await submitStoreRequest(
        payload,
        selectedWarehouse.value,
        selectedReceivingStore.value,
      );

      if (response.status !== 200) {
        throw new Error(`Unexpected response status: ${response.status}`);
      }

      clearCurrentRequest();

      showNotification("Store request submitted successfully.", "success");
    } catch (error) {
      console.error("Store request submission failed:", error);

      showNotification(
        "Something went wrong while submitting the request.",
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    clearCurrentRequest,
    notes,
    requestItems,
    selectedReceivingStore,
    selectedWarehouse,
    showNotification,
  ]);

  /**
   * Clears search when Escape is pressed.
   */
  const handleSearchKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>): void => {
      if (event.key === "Escape") {
        setSearch("");
      }
    },
    [],
  );

  return (
    <>
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          minHeight: "100%",
          boxSizing: "border-box",
          overflowX: "hidden",
          bgcolor: "#F7F9F6",
          p: {
            xs: 1,
            sm: 2,
            md: 3,
          },
        }}
      >
        <Stack
          spacing={2}
          sx={{
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          }}
        >
          {/* Page header */}
          <Paper
            variant="outlined"
            sx={{
              width: "100%",
              minWidth: 0,
              boxSizing: "border-box",
              borderRadius: 3,
              p: {
                xs: 1.5,
                md: 2,
              },
            }}
          >
            <Stack spacing={1.75}>
              <Stack
                direction={{
                  xs: "column",
                  md: "row",
                }}
                spacing={1.5}
                alignItems={{
                  xs: "flex-start",
                  md: "center",
                }}
                justifyContent="space-between"
              >
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                  sx={{ minWidth: 0 }}
                >
                  <Box
                    sx={{
                      width: 46,
                      height: 46,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 2.5,
                      bgcolor: brandColors.header,
                      color: "common.white",
                    }}
                  >
                    <Inventory2OutlinedIcon />
                  </Box>

                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        letterSpacing: "-0.02em",
                      }}
                    >
                      New Store Request
                    </Typography>

                    <Typography variant="body2" color="text.secondary">
                      Select products and quantities for the receiving store.
                    </Typography>
                  </Box>
                </Stack>

                <Stack
                  direction="row"
                  spacing={0.75}
                  useFlexGap
                  flexWrap="wrap"
                >
                  <Chip
                    icon={<ShoppingCartCheckoutRoundedIcon />}
                    label={`${requestItems.length} products`}
                    color={requestItems.length > 0 ? "success" : "default"}
                    variant={requestItems.length > 0 ? "filled" : "outlined"}
                  />

                  <Chip
                    label={`${totalRequestPacks} packs`}
                    variant="outlined"
                  />
                </Stack>
              </Stack>

              <Box
                sx={{
                  width: "100%",
                  minWidth: 0,
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "repeat(2, minmax(0, 1fr))",
                  },
                  gap: 1.25,
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    minWidth: 0,
                    p: 1.25,
                    borderRadius: 2.5,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Sending warehouse
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.25,
                      fontWeight: 750,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {selectedWarehouse?.label ?? "No warehouse available"}
                  </Typography>
                </Paper>

                <FormControl fullWidth size="small">
                  <InputLabel>Receiving store</InputLabel>

                  <Select
                    value={selectedReceivingStore?.value ?? ""}
                    label="Receiving store"
                    onChange={handleReceivingStoreChange}
                  >
                    {stores.map((store) => (
                      <MenuItem key={store.value} value={store.value}>
                        {store.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <TextField
                fullWidth
                size="small"
                placeholder="Search products, categories, subcategories"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={handleSearchKeyDown}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon fontSize="small" />
                      </InputAdornment>
                    ),

                    endAdornment: search ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          aria-label="Clear search"
                          onClick={() => setSearch("")}
                        >
                          <CloseRoundedIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : undefined,
                  },
                }}
              />
            </Stack>
          </Paper>

          {loading ? (
            <Paper
              variant="outlined"
              sx={{
                width: "100%",
                minHeight: 350,
                display: "grid",
                placeItems: "center",
                borderRadius: 3,
              }}
            >
              <Stack spacing={1.25} alignItems="center">
                <CircularProgress />

                <Typography variant="body2" color="text.secondary">
                  Loading request products...
                </Typography>
              </Stack>
            </Paper>
          ) : groupedProducts.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                width: "100%",
                p: 5,
                boxSizing: "border-box",
                borderRadius: 3,
                textAlign: "center",
              }}
            >
              <SearchOffRoundedIcon
                sx={{
                  fontSize: 48,
                  color: "text.disabled",
                }}
              />

              <Typography
                variant="h6"
                sx={{
                  mt: 1,
                  fontWeight: 750,
                }}
              >
                No products found
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Try another product, category, or subcategory.
              </Typography>

              {search && (
                <Button
                  variant="outlined"
                  onClick={() => setSearch("")}
                  sx={{
                    mt: 2,
                    borderRadius: 2,
                    textTransform: "none",
                  }}
                >
                  Clear search
                </Button>
              )}
            </Paper>
          ) : (
            <Box
              sx={{
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  lg: "minmax(0, 1fr) 340px",
                },
                gap: 2,
                alignItems: "start",
              }}
            >
              {/* Product catalogue */}
              <Stack
                spacing={1.5}
                sx={{
                  width: "100%",
                  minWidth: 0,
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    width: "100%",
                    minWidth: 0,
                    boxSizing: "border-box",
                    borderRadius: 3,
                    p: {
                      xs: 1.25,
                      sm: 1.5,
                    },
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      mb: 1,
                      fontWeight: 750,
                    }}
                  >
                    Categories
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={0.75}
                    useFlexGap
                    flexWrap="wrap"
                  >
                    {groupedProducts.map((category) => {
                      const isSelected =
                        selectedCategory?.name === category.name;

                      return (
                        <Button
                          key={category.name}
                          size="small"
                          variant={isSelected ? "contained" : "outlined"}
                          onClick={() => handleCategorySelect(category)}
                          sx={{
                            maxWidth: "100%",
                            minHeight: 36,
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 700,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {category.name}
                          </Box>

                          <Box
                            component="span"
                            sx={{
                              ml: 0.75,
                              px: 0.75,
                              py: 0.1,
                              borderRadius: 5,
                              bgcolor: isSelected
                                ? "rgba(255,255,255,0.2)"
                                : "action.hover",
                              fontSize: 12,
                            }}
                          >
                            {category.productCount}
                          </Box>
                        </Button>
                      );
                    })}
                  </Stack>
                </Paper>

                {selectedCategory && (
                  <Paper
                    variant="outlined"
                    sx={{
                      width: "100%",
                      minWidth: 0,
                      boxSizing: "border-box",
                      borderRadius: 3,
                      p: {
                        xs: 1.25,
                        sm: 1.5,
                      },
                    }}
                  >
                    <Stack
                      direction={{
                        xs: "column",
                        sm: "row",
                      }}
                      spacing={1}
                      alignItems={{
                        xs: "flex-start",
                        sm: "center",
                      }}
                      justifyContent="space-between"
                      sx={{ mb: 1.25 }}
                    >
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                          {selectedCategory.name}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          {selectedCategory.productCount} available products
                        </Typography>
                      </Box>

                      <Chip
                        label={`${selectedCategory.subcategories.length} subcategories`}
                        variant="outlined"
                      />
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={0.75}
                      useFlexGap
                      flexWrap="wrap"
                    >
                      {selectedCategory.subcategories.map((subcategory) => {
                        const isSelected =
                          selectedSubcategory?.name === subcategory.name;

                        return (
                          <Chip
                            key={subcategory.name}
                            clickable
                            color={isSelected ? "primary" : "default"}
                            variant={isSelected ? "filled" : "outlined"}
                            label={`${subcategory.name} · ${subcategory.products.length}`}
                            onClick={() =>
                              handleSubcategorySelect(subcategory.name)
                            }
                            sx={{
                              maxWidth: "100%",
                              height: 36,
                              fontWeight: 650,
                              "& .MuiChip-label": {
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              },
                            }}
                          />
                        );
                      })}
                    </Stack>
                  </Paper>
                )}

                {selectedSubcategory && (
                  <Box
                    sx={{
                      width: "100%",
                      minWidth: 0,
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(min(100%, 310px), 1fr))",
                      gap: 1.25,
                    }}
                  >
                    {selectedSubcategory.products.map((product) => (
                      <ProductCard
                        key={product.product_id}
                        product={product}
                        selected={selectedProductMap.get(product.product_id)}
                        onQuantityChange={handleQuantityChange}
                        onAdd={handleAddProduct}
                      />
                    ))}
                  </Box>
                )}
              </Stack>

              {/* Desktop request summary */}
              <Box
                sx={{
                  display: {
                    xs: "none",
                    lg: "block",
                  },
                  minWidth: 0,
                }}
              >
                <RequestSummary
                  requestItems={requestItems}
                  totalRequestPacks={totalRequestPacks}
                  notes={notes}
                  receivingStoreSelected={Boolean(selectedReceivingStore)}
                  onNotesChange={setNotes}
                  onRemove={handleRemoveRequestItem}
                  onReview={handleOpenReview}
                  onReset={handleResetRequest}
                />
              </Box>
            </Box>
          )}

          {/* Mobile request controls */}
          <Paper
            variant="outlined"
            sx={{
              display: {
                xs: "block",
                lg: "none",
              },
              position: "sticky",
              bottom: 8,
              zIndex: 10,
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
              borderRadius: 3,
              p: 1.25,
              boxShadow: "0 10px 30px rgba(20, 50, 20, 0.18)",
            }}
          >
            <Stack spacing={1}>
              <TextField
                fullWidth
                size="small"
                label="Request notes"
                value={notes}
                onChange={(event) => setNotes(event.target.value)}
              />

              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
              >
                <Box>
                  <Typography variant="body2" fontWeight={750}>
                    {requestItems.length} products
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    {totalRequestPacks} packs
                  </Typography>
                </Box>

                <Stack direction="row" spacing={0.75}>
                  <Button
                    color="inherit"
                    variant="outlined"
                    disabled={requestItems.length === 0 && notes.length === 0}
                    onClick={handleResetRequest}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                    }}
                  >
                    Clear
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={<ShoppingCartCheckoutRoundedIcon />}
                    disabled={
                      requestItems.length === 0 || !selectedReceivingStore
                    }
                    onClick={handleOpenReview}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 700,
                    }}
                  >
                    Review
                  </Button>
                </Stack>
              </Stack>
            </Stack>
          </Paper>
        </Stack>
      </Box>

      {/* Review dialog */}
      <Dialog
        open={reviewOpen}
        onClose={() => !submitting && setReviewOpen(false)}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        slotProps={{
          paper: {
            sx: {
              width: "100%",
              maxWidth: {
                xs: "100%",
                md: 850,
              },
              borderRadius: {
                xs: 0,
                md: 3,
              },
            },
          },
        }}
      >
        <DialogTitle>
          <Stack
            direction="row"
            spacing={2}
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h6" fontWeight={800}>
                Review Store Request
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Confirm the request before submitting.
              </Typography>
            </Box>

            <IconButton
              aria-label="Close request review"
              disabled={submitting}
              onClick={() => setReviewOpen(false)}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
              },
              gap: 1.25,
              mb: 2,
            }}
          >
            <Paper
              variant="outlined"
              sx={{
                minWidth: 0,
                p: 1.5,
                borderRadius: 2.5,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Sending warehouse
              </Typography>

              <Typography
                variant="body2"
                fontWeight={750}
                sx={{
                  mt: 0.25,
                  overflowWrap: "anywhere",
                }}
              >
                {selectedWarehouse?.label ?? "Not selected"}
              </Typography>
            </Paper>

            <Paper
              variant="outlined"
              sx={{
                minWidth: 0,
                p: 1.5,
                borderRadius: 2.5,
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Receiving store
              </Typography>

              <Typography
                variant="body2"
                fontWeight={750}
                sx={{
                  mt: 0.25,
                  overflowWrap: "anywhere",
                }}
              >
                {selectedReceivingStore?.label ?? "Not selected"}
              </Typography>
            </Paper>
          </Box>

          <Paper
            variant="outlined"
            sx={{
              borderRadius: 2.5,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 1.25,
                bgcolor: brandColors.header,
                color: "common.white",
              }}
            >
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="subtitle2" fontWeight={750}>
                  Requested products
                </Typography>

                <Typography variant="body2">
                  {requestItems.length} products · {totalRequestPacks} packs
                </Typography>
              </Stack>
            </Box>

            <Stack
              divider={<Divider />}
              sx={{
                maxHeight: "45vh",
                overflowY: "auto",
              }}
            >
              {requestItems.map((item) => (
                <Stack
                  key={item.product_id}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ p: 1.5 }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      sx={{
                        overflowWrap: "anywhere",
                      }}
                    >
                      {item.name}
                    </Typography>

                    <Typography variant="caption" color="text.secondary">
                      {item.qty_packed > 0 && (
                        <>
                          {item.qty_packed} ×{" "}
                          {getConfiguredPackLabel(item.packed, item.unit)}
                        </>
                      )}
                      {item.qty_packed > 0 && item.qty_unpacked > 0 && " · "}
                      {item.qty_unpacked > 0 && (
                        <>
                          {item.qty_unpacked} ×{" "}
                          {getSingleUnitPackLabel(item.unit)}
                        </>
                      )}
                    </Typography>
                  </Box>

                  <Tooltip title="Remove from request">
                    <IconButton
                      size="small"
                      color="error"
                      disabled={submitting}
                      onClick={() => handleRemoveRequestItem(item.product_id)}
                    >
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              ))}
            </Stack>
          </Paper>

          {notes && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <strong>Request note:</strong> {notes}
            </Alert>
          )}
        </DialogContent>

        <Divider />

        <DialogActions
          sx={{
            px: {
              xs: 2,
              md: 3,
            },
            py: 2,
            gap: 1,
          }}
        >
          <Button
            color="inherit"
            variant="outlined"
            disabled={submitting}
            onClick={() => setReviewOpen(false)}
            sx={{
              borderRadius: 2,
              textTransform: "none",
            }}
          >
            Back
          </Button>

          <Button
            variant="contained"
            disabled={submitting || requestItems.length === 0}
            startIcon={
              submitting ? (
                <CircularProgress size={17} color="inherit" />
              ) : (
                <ShoppingCartCheckoutRoundedIcon />
              )
            }
            onClick={() => void handleSubmit()}
            sx={{
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 750,
            }}
          >
            {submitting ? "Submitting..." : "Submit request"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset confirmation */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Clear store request?</DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            All selected products, quantities, store selection, and request
            notes will be removed.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            color="inherit"
            onClick={() => setResetDialogOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={handleConfirmReset}
            sx={{
              borderRadius: 2,
              textTransform: "none",
            }}
          >
            Clear request
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={3500}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        onClose={() =>
          setNotification((current) => ({
            ...current,
            open: false,
          }))
        }
      >
        <Alert
          severity={notification.severity}
          variant="filled"
          onClose={() =>
            setNotification((current) => ({
              ...current,
              open: false,
            }))
          }
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default RequestInventory;
